import DataTable from "@components/DataTable";
import {
  DIRECTION_COLOR,
  biasSentence,
  delta,
  formatDeliveryDate,
  hours,
  variancePercent,
} from "@common/estimateAccuracy";
import { ServiceDelivery } from "../../../types";
import { useStyles } from "./styles";

interface Props {
  services: ServiceDelivery[];
}

// `id` exists only to satisfy DataTable's `HasID` generic constraint.
// `DataTable` calls `useReactTable` with no `getRowId`, so this value is never
// read as a React key or a stable identity -- it's a synthetic per-render
// index, not a real service identifier, and must not be treated as one.
const withIds = (services: ServiceDelivery[]) =>
  services.map((service, index) => ({ ...service, id: String(index) }));

const ServiceDeliveryTable = ({ services }: Props) => {
  const styles = useStyles();

  const header = (label: string) => (
    <div className={styles.columnHeader}>{label}</div>
  );

  // `services` arrives worst-overrun-first: the API sorts by
  // `-delta_hours` (EstimateAccuracy::ProjectDelivery#services,
  // project_delivery.rb:24). Do not re-sort here -- a client-side sort would
  // either duplicate that ordering or silently fight it.
  const columns = [
    {
      id: "name",
      header: () => header("Service"),
      cell: (props) => props.row.original.name,
    },
    {
      id: "status",
      header: () => header("Status"),
      // An unsettled service is listed and labelled rather than hidden: its
      // estimate is whole while its hours are partial, which is why it does
      // not count toward the project totals.
      cell: (props) =>
        props.row.original.settled ? "Delivered" : "Still delivering",
    },
    {
      id: "coverage",
      header: () => header("Coverage"),
      // Coverage is only meaningful for a settled service: an unsettled one
      // has not finished recording yet, so a partial subservice count there
      // is expected, not a gap -- the Status column already says "Still
      // delivering" for it.
      cell: (props) =>
        props.row.original.settled && props.row.original.coverage === "partial"
          ? "Some subservices were never recorded"
          : "—",
    },
    {
      id: "estimated-hours",
      header: () => header("Sold Hrs"),
      cell: (props) => hours(props.row.original.estimatedHours),
    },
    {
      id: "actual-hours",
      header: () => header("Actual Hrs"),
      cell: (props) => hours(props.row.original.actualHours),
    },
    {
      id: "delta-hours",
      header: () => header("Delta"),
      cell: (props) => delta(props.row.original.deltaHours),
    },
    {
      id: "variance-percentage",
      header: () => header("Variance"),
      cell: (props) => variancePercent(props.row.original.variancePercentage),
    },
    {
      id: "closed-on",
      header: () => header("Closed"),
      cell: (props) => formatDeliveryDate(props.row.original.closedOn) || "—",
    },
    {
      id: "catalog-bias",
      header: () => header("Catalog Bias"),
      // How this catalog service estimates everywhere else, so a one-off miss
      // reads differently from a service that always runs long.
      cell(props) {
        const bias = props.row.original.catalogBias;
        if (!bias) return "No delivery history yet";

        return (
          <span style={{ color: DIRECTION_COLOR[bias.direction] }}>
            {biasSentence(bias.direction, bias.biasPercentage)}
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      data={withIds(services)}
      columns={columns}
      striped
      hover
      classForTable="serviceDeliveryTable"
    />
  );
};

export default ServiceDeliveryTable;
